import React, { useEffect, useMemo, useState } from "react";
import { AuthConsumer, useAuth } from "../../shared/contexts/AuthContext";
import {
    Button,
    Dropdown,
    Flex,
    Grid,
    Input,
    Layout,
    Menu,
    MenuProps,
    Spin,
    Typography,
    AutoComplete,
    Avatar,
    Switch,
} from "antd";
import { VscGraph } from "react-icons/vsc";
import AppRoutes from "./AppRoutes";
import {
    FaUsers,
} from "react-icons/fa6";
import {
    FaUserCircle,
} from "react-icons/fa";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
    PATH_DONORS,
    PATH_DASHBOARD,
    PATH_USERS,
    PATH_PROFILE,
} from "./RouteConstants";
import {
    CloseOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { DONOR_SERVICE } from "../../shared/services/donor-service";
import { PiUsersFill } from "react-icons/pi";
import { AUTH_SERVICE } from "../../shared/services/auth-service";
import hff_logo from "../../assets/images/hff_logo.png"
import hff_donors_log from "../../assets/images/hff_donors_logo.png"
import "./AppLayout.scss";

const { Content, Sider, Header } = Layout;
const { useBreakpoint } = Grid;

interface SearchUser {
    first_name: string;
    last_name: string;
    id: string;
    email: string;
    avatar?: string;
    address_1: string;
    address_2: string;
    mobile_phone: string;
    home_phone: string;
    additional_phones: string;
}

interface SearchResponse {
    success: boolean;
    data: {
        donors: SearchUser[];
        totalDonors: number;
        totalPages: number;
        currentPage: number;
        limit: number;
    };
}

function useDebounce<T extends (...args: any[]) => void>(
    callback: T,
    delay: number
) {
    const timeoutRef = React.useRef<NodeJS.Timeout>();

    return React.useCallback(
        (...args: Parameters<T>) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [callback, delay]
    );
}

const loadingIndicatorStyle: React.CSSProperties = {
    position: 'absolute',
    right: '11px',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
};

export default function AppLayout({ children }: any) {

    const [collapsed, setCollapsed] = useState(false);
    const screens = useBreakpoint();
    const { Text } = Typography;

    const navigate = useNavigate();
    const { userRole, logout, user, type, setAppType } = useAuth();




    const [activeLabel, setActiveLabel] = useState<string>("")
    const [selectedKey, setSelectedKey] = useState<string>("1");
    const location = useLocation();

    const [searchOptions, setSearchOptions] = useState<{ label: React.ReactNode; value: string }[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchPage, setSearchPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [inputValue, setInputValue] = useState('');

    const handleLogoutClick = async () => {
        try {
            const response = await AUTH_SERVICE.logout("/api/crm/logout");
            console.log(response);
            logout();
            navigate("/login");
        } catch (error) {
            console.error('Logout error:', error);
        }

    }

    const handleNavigation = (path: string, key: string) => {
        if (screens.xs) {
            setCollapsed(false);
        }
        setSelectedKey(key);
        navigate(path);
    };

    const handleSearch = useDebounce(async (value: string) => {

        setSearchQuery(value);
        setSearchPage(1);

        if (!value) {
            setSearchOptions([]);
            setHasMore(false);
            return;
        }

        setSearchLoading(true);
        try {
            const response = await DONOR_SERVICE.searchDonors('api/crm/donors', {
                searchQuery: value?.trim(),
                page: 1,
                limit: 10
            }) as SearchResponse;

            if (response?.success) {
                const options = response?.data?.donors ?? []
                const searchResults = options.map((donor: SearchUser) => {
                    const firstName = donor?.first_name ?? '';
                    const lastName = donor?.last_name ?? '';
                    const email = donor?.email ?? '';
                    const address1 = donor?.address_1 ?? '';
                    const address2 = donor?.address_2 ?? '';
                    const mobilePhone = (donor?.mobile_phone ?? '') || (donor?.home_phone ?? '') || (donor?.additional_phones ?? '');
                    const avatarChar = firstName.charAt(0) || '?';

                    return {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Avatar size="small">{avatarChar}</Avatar>
                                <div>
                                    <div style={{ fontWeight: 500 }}>
                                        {firstName} {lastName}
                                    </div>
                                    {email && (
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            {email}
                                        </div>
                                    )}
                                    {(address1 || address2) && (
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            {address1}{address2 ? `, ${address2}` : ''}
                                        </div>
                                    )}
                                    {mobilePhone && (
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            {mobilePhone}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ),
                        value: donor?.id ?? '',
                    };
                });

                
                const filteredOptions = searchResults.filter((option) => !searchOptions.some((o) => o.value === option.value));

                setSearchOptions(filteredOptions);
                setHasMore(response.data.currentPage < response.data.totalPages);
            }
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setSearchLoading(false);
        }
    }, 300);

    const loadMore = async () => {


        console.log("load more", searchQuery, hasMore, searchLoading)


        if (!searchQuery || !hasMore || searchLoading) {
            return;
        }

        setSearchLoading(true);
        try {
            const nextPage = searchPage + 1;
            const response = await DONOR_SERVICE.searchDonors('api/crm/donors', {
                searchQuery,
                page: nextPage,
                limit: 10
            }) as SearchResponse;

            if (response?.success && response?.data?.donors?.length > 0) {
                const newOptions = response?.data?.donors?.map((donor: SearchUser) => ({
                    label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                            <Avatar size="small">{donor.first_name.charAt(0)}</Avatar>
                            <div>
                                <div style={{ fontWeight: 500 }}>{donor.first_name} {donor.last_name}</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    {donor.address_1}{donor.address_2 ? `, ${donor.address_2}` : ''}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>{donor.email}</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>{donor.mobile_phone}</div>
                            </div>
                        </div>
                    ),
                    value: donor.id,
                }));

                const filteredOptions = newOptions.filter((option) => !searchOptions.some((o) => o.value === option.value));

                setSearchOptions(prev => [...prev, ...filteredOptions]);
                setSearchPage(nextPage);
                setHasMore(response.data.currentPage < response.data.totalPages);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Load more error:', error);
            setHasMore(false);
        } finally {
            setSearchLoading(false);
        }
    };

    const onSelect = (value: string) => {
        navigate(`/donors/donor/${value}`);
        setSearchOptions([]);
        setSearchQuery('');
        setInputValue('');
    };

    const extractBasePath = (path: string | null): string => {
        try {
            if (!path || typeof path !== "string") {
                throw new Error("Invalid input: Path must be a non-empty string.");
            }

            const normalizedPath = path.startsWith("/") ? path : `/${path}`;
            const segments = normalizedPath.split("/").filter(Boolean);

            return segments.length > 0 ? `/${segments[0]}` : "/";
        } catch (error: any) {
            console.error("Error extracting base path:", error.message);
            return "/";
        }
    };

    const items: MenuProps["items"] = [
        {
            key: "1",
            label: user?.name || "-",
            icon: <UserOutlined />,
            onClick: () => navigate("/profile"),
        },
        {
            type: "divider",
        },
        {
            key: "4",
            label: "Logout",
            icon: <LogoutOutlined />,
            onClick: handleLogoutClick,
        },
    ];

    const siderItems = [
        {
            key: "1",
            icon: <VscGraph color="black" size={15} />,
            label: "Dashboard",
            onClick: () => handleNavigation(PATH_DASHBOARD, "1"),
            roles: ["admin", "user", "super_admin"],
        },
        {
            key: "2",

            icon: <FaUsers color="black" size={15} />,
            label: "Donors",
            onClick: () => handleNavigation(PATH_DONORS, "2"),
            roles: ["admin", "user", "super_admin"],
        },
        {
            key: "3",
            icon: <PiUsersFill color="black" size={15} />,
            label: "Users",
            onClick: () => handleNavigation(PATH_USERS, "3"),
            roles: ["admin", "super_admin"],
        },
        // {
        //     key: "4",
        //     icon: <SettingFilled color="black" size={15} />,
        //     label: "User Roles",
        //     onClick: () => handleNavigation(PATH_USER_ROLES, "4"),
        //     roles: ["super_admin", "user"],
        // },
        // {
        //     key: "5",
        //     icon: <FaProjectDiagram color="black" size={15} />,
        //     label: "Workflows",
        //     onClick: () => handleNavigation(PATH_WORKFLOWS, "5"),
        //     roles: ["super_admin"],
        // },
        // {
        //     key: "6",
        //     icon: <FaDollarSign color="black" size={15} />,
        //     label: "Finances",
        //     onClick: () => handleNavigation(PATH_FINANCES, "6"),
        //     roles: ["super_admin"],
        // },
        // {
        //     key: "7",
        //     icon: <FaCog color="black" size={15} />,
        //     label: "Settings",
        //     onClick: () => handleNavigation(PATH_SETTINGS, "7"),
        //     roles: ["super_admin", "user"], 
        // }
    ];

    const renderHeader2 = () => {
        return (
            <>
                <Header className="bg-header" style={
                    {
                        padding: screens.xs ? "0px" : "0px 8px",
                        borderLeft: "1px solid white",
                    }} >
                    <Flex align="center" justify="space-between">

                        <Flex align="center">
                            <Button
                                type="text"
                                icon={collapsed ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                                onClick={() => setCollapsed(!collapsed)}
                                style={{
                                    fontSize: '16px',
                                    width: 64,
                                    height: 64,
                                    color: "white"
                                }}
                            />

                            <Text className={screens.xs ? "fs-14" : "fs-18"} style={{
                                fontWeight: "bolder",
                                color: "white"
                            }} >

                                {activeLabel}
                            </Text>

                        </Flex>

                        <Flex className="search-result" justify="space-between" align=" " gap={24} style={{ width: "50%" }}>
                            {/* <div style={{ position: 'relative', width: '100%' }}> */}
                            <AutoComplete
                                listHeight={screens.xs ? 300 : 600}
                                value={inputValue}
                                open={true}
                                onChange={setInputValue}
                                style={{ width: '100%' }}
                                options={searchOptions}
                                onSearch={handleSearch}
                                className={"search-result br-4"}
                                onSelect={onSelect}
                                onBlur={() => {
                                    setTimeout(() => {
                                        setSearchOptions([]);
                                        setSearchQuery('');
                                        setHasMore(false);
                                        setSearchPage(1);
                                    }, 200);
                                }}
                                onPopupScroll={(e) => {
                                    const target = e.target as HTMLElement;
                                    if (
                                        target.scrollHeight - (target.scrollTop + target.clientHeight) < 50 &&
                                        !searchLoading &&
                                        hasMore
                                    ) {
                                        loadMore();
                                    }
                                }}
                                notFoundContent={
                                    searchQuery ? (
                                        searchLoading ? (
                                            <div style={{ padding: '8px', textAlign: 'center' }}>
                                                <Spin size="small" />
                                            </div>
                                        ) : searchOptions.length === 0 ? (
                                            <div style={{ padding: '8px', textAlign: 'center', color: '#666' }}>
                                                No donors found
                                            </div>
                                        ) : null
                                    ) : null
                                }
                            >
                                <Input
                                    placeholder="Search donors..."
                                    className="no-border-radius input-height br-4"
                                    prefix={
                                        <CiSearch
                                            size={18}
                                            style={{ color: "rgba(0,0,0,.45)", fontSize: "18px" }}
                                        />
                                    }
                                    suffix={searchLoading && (
                                        <div style={loadingIndicatorStyle}>
                                            <Spin size="small" />
                                        </div>
                                    )}
                                />
                            </AutoComplete>
                            {/* </div> */}

                            {!screens.xs && <Flex align="center" gap={8}>
                                <Switch
                                    checked={type === "hff"}
                                    checkedChildren="HFF"
                                    unCheckedChildren="HFA"
                                    className="custom-switch"
                                    onChange={(checked) => setAppType(checked ? "hff" : "hfa")}
                                    style={{ backgroundColor: "#133E87" }}
                                />
                            </Flex>
                            }



                            <Dropdown menu={{ items }}>

                                <Button
                                    // hidden={isMobile || isTablet}
                                    type="text"
                                    icon={<FaUserCircle size={26} />}
                                    onClick={() => { }}
                                    className="no-border-radius "
                                    style={{
                                        fontSize: "16px",
                                        padding: "5px",
                                        width: 44,
                                        height: 44,
                                        color: "white",
                                        border: "1px solid white"
                                    }}
                                />
                            </Dropdown>
                        </Flex>

                    </Flex>
                </Header>
            </>
        )
    }

    const renderBody2 = () => {
        return (
            <>
                <Content
                    className="bg-primary"
                    style={{
                        margin: '12px 8px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }}
                >
                    <AppRoutes />
                </Content>
            </>
        )
    }

    const filteredSiderItems = useMemo(() => {
        return siderItems.filter(item => item.roles.includes(userRole));
    }, [siderItems, userRole]);

    useEffect(() => {

        console.log(location.pathname)
        const path = extractBasePath(location.pathname)
        if (path == PATH_DONORS) {
            setActiveLabel("Donors")
            setSelectedKey("2")
        }
        if (path == PATH_DASHBOARD) {
            setActiveLabel("Dashboard")
            setSelectedKey("1")
        }
        if (path == PATH_USERS) {
            setActiveLabel("Users")
            setSelectedKey("3")
        }
        if (path == PATH_PROFILE) {
            setActiveLabel("Profile")
            setSelectedKey("4")
        }
    }, [location.pathname]);


    return (
        <AuthConsumer>
            {({ isAuth }) => {
                return (
                    <>
                        <Layout
                        // className={`bg-dark `}
                        >
                            <Sider
                                trigger={null}
                                collapsible
                                collapsed={(screens.xs || screens.sm) ? !collapsed : false}
                                theme="dark"
                                collapsedWidth={(screens.xs) ? 0 : 80}
                                width={250}
                            >

                                <Flex justify="space-between" gap={8} align="center">
                                    {screens.xs &&
                                        <Switch
                                            checked={type === "hff"}
                                            checkedChildren="HFF"
                                            unCheckedChildren="HFA"
                                            size="default"
                                            className="custom-switch"
                                            onChange={(checked) => {
                                                setAppType(checked ? "hff" : "hfa")
                                                setCollapsed(false)
                                            }}
                                            style={{ backgroundColor: "#133E87", margin: "12px" }}
                                        />
                                    }
                                    <Button
                                        type="text"
                                        hidden={!screens.xs}
                                        icon={collapsed ? <CloseOutlined /> : <MenuUnfoldOutlined />}
                                        onClick={() => setCollapsed(!collapsed)}
                                        style={{
                                            fontSize: '16px',
                                            width: 64,
                                            height: 64,
                                            textAlign: "right",
                                            color: "white"
                                        }}
                                    />
                                </Flex>
                                <div className="demo-logo-vertical">
                                    {!collapsed ? <img width={42} src={hff_logo} className="fade-in" /> : <img width={192} src={hff_donors_log} className="fade-in" />}
                                </div>
                                <Menu
                                    theme="dark"
                                    mode="inline"
                                    defaultSelectedKeys={[selectedKey]}
                                    selectedKeys={[selectedKey]}
                                    items={filteredSiderItems}
                                // items={siderItems}
                                />
                            </Sider>


                            <Layout
                                className={(collapsed && screens.xs) ? "blur-screen" : ""}
                                style={{ height: "100vh", scrollbarWidth: 'none', }}
                            >
                                {renderHeader2()}
                                {renderBody2()}
                            </Layout>
                        </Layout>
                    </>
                );
            }}
        </AuthConsumer>
    );
}
